/**
 * 보안 메모 카드 컴포넌트
 * 개별 메모 표시
 */

import React from 'react';
import { useSecureNotes } from '../../contexts/SecureNotesContext';
import {
  Lock,
  Star,
  Archive,
  Paperclip,
  Calendar,
  Tag,
  Edit,
  Trash2,
  Eye,
  FolderInput
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

function SecureNoteCard({ note, onEdit, onView, onDelete, onMove }) {
  const { toggleFavorite, toggleArchive } = useSecureNotes();
  
  // 날짜 포맷
  const formatDate = (date) => {
    if (!date) return '';
    
    try {
      // Firestore Timestamp 또는 일반 Date 객체 처리
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      
      // 유효한 날짜인지 확인
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date:', date);
        return '';
      }
      
      return format(dateObj, 'MM/dd HH:mm', { locale: ko });
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };
  
  // 미리보기 텍스트 생성
  const getPreviewText = () => {
    if (note.isPinProtected) {
      return 'PIN으로 보호된 메모입니다.';
    }
    
    if (note.plainTextContent && typeof note.plainTextContent === 'string') {
      return note.plainTextContent.slice(0, 100) + 
        (note.plainTextContent.length > 100 ? '...' : '');
    }
    
    return '내용 없음';
  };
  
  // 카테고리 색상
  const getCategoryColor = () => {
    switch (note.category) {
      case 'personal': return '#6c757d';
      case 'work': return '#007bff';
      case 'finance': return '#28a745';
      case 'health': return '#dc3545';
      default: return '#6c757d';
    }
  };
  
  // 즐겨찾기 토글
  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    try {
      await toggleFavorite(note.id);
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };
  
  // 아카이브 토글
  const handleToggleArchive = async (e) => {
    e.stopPropagation();
    try {
      await toggleArchive(note.id);
    } catch (err) {
      console.error('Error toggling archive:', err);
    }
  };
  
  return (
    <div 
      className={`secure-note-card ${note.isArchived ? 'archived' : ''}`}
      onClick={onView}
    >
      {/* 헤더 */}
      <div className="note-card-header">
        <div className="note-card-title">
          {note.isPinProtected && <Lock size={16} />}
          <h3>{note.title || '제목 없음'}</h3>
        </div>
        
        <div className="note-card-actions">
          <button
            className={`icon-button ${note.isFavorite ? 'active' : ''}`}
            onClick={handleToggleFavorite}
            title="즐겨찾기"
          >
            <Star size={16} fill={note.isFavorite ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>
      
      {/* 카테고리 및 태그 */}
      <div className="note-card-meta">
        {note.category && (
          <span 
            className="note-category"
            style={{ backgroundColor: getCategoryColor() }}
          >
            {note.category}
          </span>
        )}
        
        {note.tags && note.tags.length > 0 && (
          <div className="note-tags">
            <Tag size={12} />
            {note.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="note-tag">
                {tag}
              </span>
            ))}
            {note.tags.length > 3 && (
              <span className="note-tag-more">+{note.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
      
      {/* 내용 미리보기 */}
      <div className="note-card-content">
        <p>{getPreviewText()}</p>
      </div>
      
      {/* 하단 정보 */}
      <div className="note-card-footer">
        <div className="note-card-info">
          {note.attachments && note.attachments.length > 0 && (
            <span className="note-attachment-count">
              <Paperclip size={12} />
              {note.attachments.length}
            </span>
          )}
          
          <span className="note-date">
            <Calendar size={12} />
            {formatDate(note.updatedAt)}
          </span>
        </div>
        
        <div className="note-card-buttons">
          {note.isArchived && (
            <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleArchive(e);
              }}
              title="아카이브 해제"
            >
              <Archive size={16} />
            </button>
          )}

          {onMove && (
            <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                onMove();
              }}
              title="폴더로 이동"
            >
              <FolderInput size={16} />
            </button>
          )}

          <button
            className="icon-button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            title="편집"
          >
            <Edit size={16} />
          </button>

          <button
            className="icon-button danger"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="삭제"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default SecureNoteCard;